import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import RichTextEditor from "@/components/RichTextEditor";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Loader2, Plus, FileText, Pencil, Trash2, ExternalLink, ArrowLeft, LayoutPanelTop, PanelBottom,
} from "lucide-react";

const EMPTY = {
  title: "",
  slug: "",
  content_html: "",
  status: "draft",
  show_in_header: false,
  show_in_footer: false,
  menu_order: 0,
  seo_title: "",
  seo_description: "",
};

function slugifyClient(text) {
  return (text || "")
    .toLowerCase()
    .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i").replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-");
}

export default function Pages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // null = list, {id?} = form
  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/pages");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing({ id: null });
    setForm(EMPTY);
    setSlugTouched(false);
  };

  const startEdit = (page) => {
    setEditing({ id: page.id });
    setForm({
      title: page.title || "",
      slug: page.slug || "",
      content_html: page.content_html || "",
      status: page.status || "draft",
      show_in_header: !!page.show_in_header,
      show_in_footer: !!page.show_in_footer,
      menu_order: page.menu_order || 0,
      seo_title: page.seo_title || "",
      seo_description: page.seo_description || "",
    });
    setSlugTouched(true);
  };

  const onTitleChange = (title) => {
    setForm((f) => ({
      ...f,
      title,
      slug: slugTouched ? f.slug : slugifyClient(title),
    }));
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Başlık gerekli");
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/pages/${editing.id}`, form);
        toast.success("Sayfa güncellendi");
      } else {
        await api.post("/pages", form);
        toast.success("Sayfa oluşturuldu");
      }
      await load();
      setEditing(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu sayfa silinsin mi?")) return;
    try {
      await api.delete(`/pages/${id}`);
      toast.success("Sayfa silindi");
      if (editing?.id === id) setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const previewUrl = useMemo(() => {
    if (!form.slug) return null;
    return `/sayfa/${form.slug}`;
  }, [form.slug]);

  if (editing) {
    return (
      <DashboardLayout>
        <div className="p-4 sm:p-6 lg:p-10" data-testid="pages-editor">
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-xl" data-testid="pages-back">
              <ArrowLeft className="mr-2 h-4 w-4" /> Listeye Dön
            </Button>
            <div className="flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sayfalar</p>
              <h1 className="font-heading text-2xl font-bold text-slate-900">{editing.id ? "Sayfayı Düzenle" : "Yeni Sayfa"}</h1>
            </div>
            {previewUrl && form.status === "published" && (
              <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-violet-600 hover:text-violet-700">
                <ExternalLink className="h-4 w-4" /> Önizle
              </a>
            )}
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="space-y-4 xl:col-span-2">
              <Card className="rounded-2xl border-slate-200/60 p-5">
                <div className="space-y-4">
                  <div>
                    <Label>Başlık</Label>
                    <Input data-testid="page-title" value={form.title} onChange={(e) => onTitleChange(e.target.value)} className="mt-1.5 rounded-xl" placeholder="Gizlilik Politikası" />
                  </div>
                  <div>
                    <Label>URL adresi (slug)</Label>
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="text-sm text-slate-400">/sayfa/</span>
                      <Input
                        data-testid="page-slug"
                        value={form.slug}
                        onChange={(e) => { setSlugTouched(true); setForm((f) => ({ ...f, slug: slugifyClient(e.target.value) })); }}
                        className="rounded-xl"
                        placeholder="gizlilik-politikasi"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="mb-2 block">İçerik</Label>
                    <RichTextEditor key={editing.id || "new"} value={form.content_html} onChange={(html) => setForm((f) => ({ ...f, content_html: html }))} />
                  </div>
                </div>
              </Card>
            </div>

            <div className="space-y-4">
              <Card className="rounded-2xl border-slate-200/60 p-5">
                <h3 className="font-heading font-semibold text-slate-900">Yayın</h3>
                <div className="mt-4 space-y-4">
                  <div>
                    <Label>Durum</Label>
                    <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                      <SelectTrigger className="mt-1.5 rounded-xl" data-testid="page-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Taslak</SelectItem>
                        <SelectItem value="published">Yayında</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Menü sırası</Label>
                    <Input type="number" value={form.menu_order} onChange={(e) => setForm((f) => ({ ...f, menu_order: Number(e.target.value) || 0 }))} className="mt-1.5 rounded-xl" data-testid="page-menu-order" />
                  </div>
                </div>
              </Card>

              <Card className="rounded-2xl border-slate-200/60 p-5">
                <h3 className="font-heading font-semibold text-slate-900">Menü Konumu</h3>
                <p className="mt-1 text-xs text-slate-500">Yayınlanan sayfalar site header ve footer menüsüne eklenebilir.</p>
                <div className="mt-4 space-y-4">
                  <label className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 text-sm text-slate-700"><LayoutPanelTop className="h-4 w-4 text-violet-600" /> Header menüsü</span>
                    <Switch checked={form.show_in_header} onCheckedChange={(v) => setForm((f) => ({ ...f, show_in_header: v }))} data-testid="page-header-menu" />
                  </label>
                  <label className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 text-sm text-slate-700"><PanelBottom className="h-4 w-4 text-violet-600" /> Footer menüsü</span>
                    <Switch checked={form.show_in_footer} onCheckedChange={(v) => setForm((f) => ({ ...f, show_in_footer: v }))} data-testid="page-footer-menu" />
                  </label>
                </div>
              </Card>

              <Card className="rounded-2xl border-slate-200/60 p-5">
                <h3 className="font-heading font-semibold text-slate-900">SEO (isteğe bağlı)</h3>
                <div className="mt-4 space-y-3">
                  <div>
                    <Label>SEO başlık</Label>
                    <Input value={form.seo_title} onChange={(e) => setForm((f) => ({ ...f, seo_title: e.target.value }))} className="mt-1.5 rounded-xl" />
                  </div>
                  <div>
                    <Label>SEO açıklama</Label>
                    <Input value={form.seo_description} onChange={(e) => setForm((f) => ({ ...f, seo_description: e.target.value }))} className="mt-1.5 rounded-xl" />
                  </div>
                </div>
              </Card>

              <div className="flex flex-col gap-2">
                <Button onClick={save} disabled={saving} data-testid="page-save" className="h-11 rounded-xl bg-violet-600 text-white hover:bg-violet-700">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
                </Button>
                {editing.id && (
                  <Button variant="outline" onClick={() => remove(editing.id)} data-testid="page-delete" className="h-11 rounded-xl text-red-600 hover:bg-red-50 hover:text-red-700">
                    <Trash2 className="mr-2 h-4 w-4" /> Sil
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="pages-page">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.05em] text-slate-500"><FileText className="h-3.5 w-3.5" /> İçerik Yönetimi</p>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">Sayfalar</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">Gizlilik politikası, kullanım koşulları gibi sayfalar oluşturun. Header ve footer menüsüne ekleyebilirsiniz.</p>
          </div>
          <Button onClick={startNew} data-testid="pages-new" className="h-11 rounded-xl bg-violet-600 text-white hover:bg-violet-700">
            <Plus className="mr-2 h-4 w-4" /> Yeni Sayfa
          </Button>
        </header>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
        ) : items.length === 0 ? (
          <Card className="rounded-2xl border-dashed border-slate-200 p-12 text-center">
            <FileText className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 font-medium text-slate-700">Henüz sayfa yok</p>
            <p className="mt-1 text-sm text-slate-500">İlk sayfanızı oluşturmak için &quot;Yeni Sayfa&quot;ya tıklayın.</p>
          </Card>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-100 bg-slate-50/80 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3">Başlık</th>
                  <th className="px-5 py-3">URL</th>
                  <th className="px-5 py-3">Durum</th>
                  <th className="px-5 py-3">Menü</th>
                  <th className="px-5 py-3 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b border-slate-50 last:border-0" data-testid={`page-row-${p.id}`}>
                    <td className="px-5 py-4 font-medium text-slate-900">{p.title}</td>
                    <td className="px-5 py-4 text-slate-500">/sayfa/{p.slug}</td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${p.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {p.status === "published" ? "Yayında" : "Taslak"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1.5">
                        {p.show_in_header && <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700">Header</span>}
                        {p.show_in_footer && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">Footer</span>}
                        {!p.show_in_header && !p.show_in_footer && <span className="text-slate-400">—</span>}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        {p.status === "published" && (
                          <a href={`/sayfa/${p.slug}`} target="_blank" rel="noopener noreferrer" className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-violet-600" aria-label="Önizle">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                        <button onClick={() => startEdit(p)} data-testid={`page-edit-${p.id}`} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-violet-600">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => remove(p.id)} data-testid={`page-delete-${p.id}`} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
